import { Payment } from ".";

export const CASH_IN_HAND_KEY = "CASH_IN_HAND";
export const CASH_IN_ISA_KEY = "CASH_IN_ISA";

export type SupportedCurrencyType =
  | "GBP"
  | "USD"
  | "EUR"
  | "INR"
  | "AED"
  | "MYR"
  | "CAD";

export const SupportedCurrencyTypes: SupportedCurrencyType[] = [
  "GBP",
  "USD",
  "EUR",
  "INR",
  "AED",
  "MYR",
  "CAD",
];

export type ZakatAssetType =
  | "CASH"
  | "CHILD_TRUST_FUND"
  | "GOLD_SILVER"
  | "BANK"
  | "SHARES"
  | "FUNDS"
  | "CRYPTO"
  | "PENSIONS"
  | "BUSINESS"
  | "PROPERTIES"
  | "AGRICULTURE_ASSETS"
  | "CUR8_PORTFOLIO"
  | "ASSETS_OWED"
  | "OTHER";

export type ZakatAssetSubtype =
  | "SHARES_SHORT_TERM"
  | "SHARES_LONG_TERM"
  | "SHARES_MIXED_INTENTION"
  | "FUNDS_SHORT_TERM"
  | "FUNDS_LONG_TERM"
  | "PENSIONS_DEFINED_CONTRIBUTION"
  | "PENSIONS_DEFINED_BENEFIT"
  | "ASSETS_OWED_STRONG_DEBT"
  | "ASSETS_OWED_WEAK_DEBT"
  | "PROPERTY_HOLDING"
  | "PROPERTY_TRADING";

export type ZakatLiabilityType =
  | "MORTGAGE"
  | "CONVENTIONAL_MORTGAGE"
  | "ISLAMIC_MORTGAGE"
  | "STUDENT_LOAN"
  | "MONEY_BORROWED"
  | "CREDIT_CARD"
  | "CAR_FINANCE"
  | "BANK_LOAN"
  | "OTHER";

export interface ZakatAsset {
  id: string;
  amount: number;
  name: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  type: ZakatAssetType | null;
  subtype?: ZakatAssetSubtype | null;
  zakat: number | null;
  // Computed fields (response-only)
  totalZakatableAmount?: number | null;
  totalZakat?: number | null;
  typeDescription?: string | null;
}

export interface ZakatLiability {
  id: string;
  amount: number;
  name: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  type: ZakatLiabilityType | null;
  zakat: number | null;
  // Computed fields (response-only)
  totalZakatableAmount?: number | null;
  totalDeduction?: number | null;
  typeDescription?: string | null;
}

export interface ZakatPayment {
  id: string;
  amount: number;
  created_at: string;
  updated_at: string;
  stripePaymentId: string | null;
  currency: SupportedCurrencyType;
  payment: Pick<
    Payment,
    "id" | "amountDue" | "amountPaid" | "status" | "dueAt" | "paidAt"
  > | null;
}

export interface Zakat {
  id: string;
  amountDue: number | null;
  calculatedAt: string | null;
  enableGiftAid: boolean | null;
  currency: SupportedCurrencyType | null;
  created_at: string;
  updated_at: string;
  investor: number;
  payments: ZakatPayment[];
  assets: ZakatAsset[];
  liabilities: ZakatLiability[];
  confirmedAt: string | null;
  // Aggregate totals (computed at response time)
  totalAssets?: number | null;
  totalZakatableAssets?: number | null;
  totalLiabilities?: number | null;
  totalLiabilityDeduction?: number | null;
}

export interface ZakatResponseDto extends Zakat {
  hasCommitmentOrConfirmedPayment: boolean;
  zakatDealId: string;
  isZakatDealLive: boolean;
}

export interface ZakatPortfolio {
  totalZakatAmount: number;
  currency: string;
  entityId: string | null;
  totalInvestmentAmount: number;
}

// Request Types
export interface CreateZakatAssetRequest {
  amount: number;
  type: ZakatAssetType;
  subtype?: ZakatAssetSubtype | null;
  name?: string; // "Cash In Hand" or "Cash In ISA"
  description?: string;
}

export interface UpdateZakatAssetRequest {
  amount?: number;
  name?: string;
  description?: string;
  subtype?: ZakatAssetSubtype | null;
}

export interface CreateZakatLiabilityRequest {
  amount: number;
  name?: string;
  description?: string;
  type: ZakatLiabilityType;
}

export interface UpdateZakatLiabilityRequest {
  amount?: number;
  name?: string;
  description?: string;
  type?: ZakatLiabilityType;
}

export interface CreateZakatPaymentRequest {
  amount: number;
  currency: SupportedCurrencyType;
  /**
   * do not use this for web when redirecting to stripe checkout page
   * use successUrl and cancelUrl instead
   * @deprecated
   */
  returnUrl?: string; // Only used for web
  successUrl?: string; // Only used for web
  cancelUrl?: string; // Only used for web
}

// export interface CreateZakatPaymentResponse {
//   id: string;
//   amount: number;
//   created_at: string;
//   updated_at: string;
//   stripePaymentId: string | null;
// }

export interface UpdateZakatRequest {
  enableGiftAid?: boolean;
  currency?: SupportedCurrencyType;
  confirmedAt?: string;
}

export interface UpdateDraftZakatRequest {
  enableGiftAid?: boolean;
  currency?: SupportedCurrencyType;
}

export type CreateZakatPaymentMobileResponse = {
  paymentIntent: string;
  ephemeralKey: string;
  stripeCustomerId: string;
  publishableKey: string;
};

export type CreateZakatPaymentWebResponse = {
  sessionId: string;
  client_secret: string;
  publishableKey: string;
};

export type CreateZakatPaymentResponse =
  | CreateZakatPaymentMobileResponse
  | CreateZakatPaymentWebResponse;

export interface ZakatBreakdownSummary {
  zakatableAssets: number;
  liabilities: number;
  nisab: number;
  totalZakatDue: number;
  formulaDescription: string;
}

export interface ZakatBreakdownItem {
  id: string;
  name: string | null;
  type: ZakatAssetType | ZakatLiabilityType | null;
  amount: number;
  zakatableAmount: number;
  zakatTreatmentRate: number;
  zakatAmount: number;
  description: string | null;
}

export interface ZakatBreakdownResponse {
  zakatId: string;
  calculatedAt: string | null;
  createdAt: string;
  currency: SupportedCurrencyType | null;
  summary: ZakatBreakdownSummary;
  assets: ZakatBreakdownItem[];
  liabilities: ZakatBreakdownItem[];
}

export interface ZakatFormulaRate {
  zakatablePercentage: number;
  zakatPercentage: number;
  zakatRate: number;
}

export interface ZakatFormulaResponse {
  assetRates: Record<ZakatAssetType, ZakatFormulaRate | null>;
  assetSubtypeRates?: Record<ZakatAssetSubtype, ZakatFormulaRate | null>;
  liabilityRates: Record<ZakatLiabilityType, number | null>;
}

// Type guard functions
export const isMobileCreateZakatPaymentResponse = (
  response: CreateZakatPaymentResponse
): response is CreateZakatPaymentMobileResponse => {
  return "paymentIntent" in response;
};

export const isWebCreateZakatPaymentResponse = (
  response: CreateZakatPaymentResponse
): response is CreateZakatPaymentWebResponse => {
  return "sessionId" in response;
};
